/**
 * Stage View Component (Game Info)
 * Displays current stage with location, hunting permit day, progress, and team status
 */

import React from 'react';
import { StageId, STAGES } from '../models/GameState';
import { narrativeManager } from '../content';
import { IoLocationSharp, IoWarning, IoAlert } from 'react-icons/io5';

interface StageViewProps {
  stage: StageId;
  huntingPermitDay: number;
  progress: number;
  variant?: 'A' | 'B' | null;
  teamName?: string;
  teamEnergyPacks?: number;
  alivePlayers?: number;
  totalPlayers?: number;
  className?: string;
}

export const StageView: React.FC<StageViewProps> = ({
  stage,
  huntingPermitDay,
  progress: _progress, // Hidden for now, rename back to 'progress' when re-enabling
  variant,
  teamName,
  teamEnergyPacks,
  alivePlayers,
  totalPlayers,
  className = ''
}) => {
  const stageData = STAGES[stage];
  const location = narrativeManager.getStageLocation(stage);
  const greeting = narrativeManager.getGreeting(huntingPermitDay);

  return (
    <div className={`bg-white border border-gray-200 rounded-xl shadow-sm ${className}`}>
      {/* Game Info Header */}
      <div className="px-4 py-3 border-b border-gray-200">
        <h3 className="text-base font-bold text-black">Game Info</h3>
      </div>

      <div className="px-4 py-3 space-y-2">
        {/* Stage */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-gray-600">Stage {stage}{variant ? `-${variant}` : ''}</span>
          <span className="text-sm font-bold text-black text-right">{stageData.name}</span>
        </div>

        {/* Hunting Permit */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-gray-600">Permit</span>
          <span className="text-sm font-semibold text-black">{greeting}</span>
        </div>

        {/* Location */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-gray-600">Location</span>
          <span className="text-sm text-black text-right flex items-center gap-1">
            <IoLocationSharp /> {location}
          </span>
        </div>

        {/* Divider */}
        {(teamName || teamEnergyPacks !== undefined || alivePlayers !== undefined) && (
          <div className="border-t border-gray-200 my-2" />
        )}

        {/* Team Name */}
        {teamName !== undefined && (
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-gray-600">Team</span>
            <span className="text-sm font-semibold text-black truncate">{teamName || 'Not Set'}</span>
          </div>
        )}

        {/* Energy Packs */}
        {teamEnergyPacks !== undefined && (
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-gray-600">Energy Packs</span>
            <span className="text-sm font-bold text-black text-lg">{teamEnergyPacks}</span>
          </div>
        )}

        {/* Alive */}
        {alivePlayers !== undefined && totalPlayers !== undefined && (
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-gray-600">Alive</span>
            <span className="text-sm font-semibold text-black">{alivePlayers}/{totalPlayers}</span>
          </div>
        )}
      </div>

      {/* Warning Messages */}
      {huntingPermitDay >= 5 && (
        <div className="bg-yellow-50 border-t border-yellow-200 px-4 py-2">
          <p className="font-semibold text-yellow-900 text-sm flex items-center gap-1">
            <IoWarning /> Permit expires soon!
          </p>
        </div>
      )}

      {stage === 5 && variant === 'B' && (
        <div className="bg-red-50 border-t border-red-200 px-4 py-2">
          <p className="font-semibold text-red-900 text-sm flex items-center gap-1">
            <IoAlert /> Fuel leak detected!
          </p>
        </div>
      )}
    </div>
  );
};

export default StageView;
